import { TokenHoldersTable } from "../TokenHoldersTable";
import { useEffect, useState } from "react";
import { TokenHolderRowData } from "../TokenHoldersTable/types";
import { paginateItems } from "~/utils/functions/paginateItems";
import { TokenHoldersPieChart } from "./TokenHoldersPieChart";
import { DEFAULT_TOKEN_HOLDERS_LIST_QUERY } from "../../consts";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { ButtonVariants, getButtonClassName } from "~/components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-solid-svg-icons";
import { getChartWatermarkClassName } from "~/modules/visualization/components/VisualizationCard/getChartWatermarkClassName";
import { cn } from "~/lib/utils";
import { BlockchainExplorerLink } from "~/components/BlockchainExplorerLink";
import { RowSizes } from "~/components/DataGrid";
import { Token } from "~/modules/assets/types";

// // // //

export function TokenHoldersDisplay(props: {
    content: {
        label: string;
        icon_url: string | null;
        token: Token | null;
    };
    rows: Array<TokenHolderRowData>;
}) {
    const { content } = props;

    const rows = props.rows
        .filter((r) => r.holder.address !== "")
        .map((r, idx) => {
            return {
                ...r,
                rank: idx + 1,
            };
        });
    const allOthersItems = props.rows.filter((r) => r.holder.address === "");

    // ENHANCEMENT - make this a hook?
    const [query, setQuery] = useState({
        ...DEFAULT_TOKEN_HOLDERS_LIST_QUERY,
        pagination: {
            page: 1,
            perPage: 15,
        },
    });

    // Reset the pagination.page value when props change
    useEffect(() => {
        setQuery({
            ...query,
            pagination: {
                page: 1,
                perPage: 15,
            },
        });
    }, [props]);

    // ENHANCEMENT - make this a react hook?
    const paginatedResponse = paginateItems({
        results: rows,
        page: query.pagination.page,
        perPage: query.pagination.perPage,
    });

    // Hide/Show "Remaining Holders"
    const [showOthers, setShowOthers] = useState(false);

    // Enable "remaining holders"
    const enableRemainingHolders = allOthersItems.length > 0;

    return (
        <div
            className="grid grid-cols-12 divide-x-0 xl:divide-x divide-y xl:divide-y-0"
            style={{ minHeight: "580px" }}
        >
            <div className="col-span-12 xl:col-span-6 xxl:col-span-7">
                <TokenHoldersTable
                    query={query}
                    setQuery={(updatedQuery) => {
                        setQuery(updatedQuery);
                    }}
                    queryResponse={{
                        results: paginatedResponse.results,
                        sort: DEFAULT_TOKEN_HOLDERS_LIST_QUERY.sort,
                        pagination: paginatedResponse.pagination,
                    }}
                />
            </div>
            <div className="col-span-12 xl:col-span-6 xxl:col-span-5 flex flex-col justify-between p-5 mt-2 xl:mt-0">
                <div className="flex flex-col gap-y-1">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <ImageLoader
                                src={content.icon_url ?? RWA_COIN_ICON}
                                className="h-7 rounded-full"
                                alt={""}
                            />
                            <p className="text-xl font-light">
                                {content.label}
                            </p>
                        </div>
                        {enableRemainingHolders && (
                            <button
                                className={getButtonClassName({
                                    variant: ButtonVariants.primaryLink,
                                    size: "sm",
                                    className: "gap-1.5",
                                })}
                                onClick={() => {
                                    setShowOthers(!showOthers);
                                }}
                            >
                                <FontAwesomeIcon
                                    className="h-4"
                                    icon={showOthers ? faEye : faEyeSlash}
                                />
                                Remaining Holders
                            </button>
                        )}
                    </div>
                    <p className="text-sm text-secondary-foreground">
                        Distribution of the top 25 holders and the combined
                        total of the remaining holders.
                    </p>
                    {content.token?.address && (
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-secondary-foreground -mt-0.5">
                                Token Address:
                            </p>
                            <BlockchainExplorerLink
                                address={content.token.address}
                                networkSlug={content.token.network?.slug ?? ""}
                                analyticsLabel="Token Holders - Contract Address Link"
                                size={RowSizes.extraSmall}
                                type="token"
                                truncate={false}
                            />
                        </div>
                    )}
                </div>
                <div
                    className={cn(
                        "flex justify-center",
                        getChartWatermarkClassName({
                            watermark: "center-50pct",
                        }),
                    )}
                >
                    <TokenHoldersPieChart
                        rows={rows.slice(0, 25)}
                        allOthers={rows
                            .slice(25, rows.length - 1)
                            .concat(allOthersItems)}
                        showOthers={showOthers}
                    />
                </div>

                {/* Spacer element - do not remove */}
                <div className="flex" />
            </div>
        </div>
    );
}
